/**
 * BulkEnrollStudentsDialog — Schüler ohne aktive Belegung auswählen
 * und gemeinsam einem bestehenden Kurs zuordnen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, UserPlus, Users } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatTime } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DateInput from '@/components/ui/date-input';
import { useClientListMode } from '@/hooks/useClientListMode';
import ListModePager from '@/components/ListModePager';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function BulkEnrollStudentsDialog({
  open,
  onClose,
  onSaved,
  prefillCourseId = null,
}) {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [courseId, setCourseId] = useState('');
  const [startDate, setStartDate] = useState(todayIso);
  const [expandCapacity, setExpandCapacity] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      let offset = 0;
      const batch = 100;
      while (true) {
        const res = await api.get('/students', {
          params: { status: 'active', has_enrollment: false, limit: batch, offset },
        });
        const chunk = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        all.push(...chunk);
        if (chunk.length < batch) break;
        offset += batch;
      }
      all.sort((a, b) =>
        `${a.last_name || ''} ${a.first_name || ''}`.localeCompare(
          `${b.last_name || ''} ${b.first_name || ''}`,
          'de',
        ));
      setStudents(all);

      const cRes = await api.get('/courses', { params: { limit: 500 } }).catch(() => ({ data: [] }));
      const cItems = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
      setCourses(cItems.map((c) => ({
        id: c.id,
        label: `${c.name || c.subject_name || 'Kurs'}${c.teacher_name ? ` · ${c.teacher_name}` : ''}${
          c.day_of_week != null && c.start_time
            ? ` · ${WEEKDAYS_SHORT[c.day_of_week] || ''} ${formatTime(c.start_time)}`
            : ''
        }`,
        max_capacity: c.max_capacity,
      })));
    } catch {
      toast.error('Schüler/Kurse konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch('');
    setStartDate(todayIso());
    setExpandCapacity(true);
    setCourseId(prefillCourseId || '');
    load();
  }, [open, prefillCourseId, load]);

  const filtered = students.filter((s) => {
    return matchesFreeText(s, search);
  });

  const {
    listMode, setListMode, pageSize, setPageSize, listPage, setListPage,
    totalCount, pageCount, visibleItems,
  } = useClientListMode(filtered, { resetKey: `${search}|${students.length}` });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    const ids = visibleItems.map((s) => s.id);
    const allOn = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSave = async () => {
    if (!courseId) {
      toast.error('Bitte einen Kurs wählen');
      return;
    }
    if (selected.size === 0) {
      toast.error('Bitte mindestens einen Schüler auswählen');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/courses/${courseId}/enrollments/bulk`, {
        student_ids: [...selected],
        start_date: startDate || null,
        expand_capacity: expandCapacity,
      });
      const { created, skipped, errors } = res.data || {};
      if (created > 0) {
        toast.success(`${created} Belegung(en) angelegt${skipped ? ` · ${skipped} übersprungen` : ''}`);
      } else {
        toast.warning(`Keine Belegung angelegt${skipped ? ` · ${skipped} übersprungen` : ''}`);
      }
      if (errors?.length) {
        toast.message(errors.slice(0, 3).join(' · '));
      }
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const visibleAllSelected = visibleItems.length > 0
    && visibleItems.every((s) => selected.has(s.id));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-brand-red" />
            Schüler ohne Belegung zuordnen
          </DialogTitle>
          <DialogDescription>
            Mehrere aktive Schüler ohne laufende Belegung einem bestehenden Kurs zuweisen.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Kurs</label>
            <Select value={courseId} onValueChange={setCourseId}>
              <SelectTrigger className="h-9" data-testid="bulk-enroll-course">
                <SelectValue placeholder={loading ? 'Laden…' : '— Kurs wählen —'} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Beginn</label>
            <DateInput
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="bulk-enroll-start"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <Checkbox
            checked={expandCapacity}
            onCheckedChange={(v) => setExpandCapacity(!!v)}
            data-testid="bulk-enroll-expand-capacity"
          />
          Kapazität bei Bedarf automatisch erhöhen
        </label>

        <Input
          placeholder="Schüler suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="bulk-enroll-search"
        />

        <div className="border rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
          <ListModePager
            listMode={listMode}
            setListMode={setListMode}
            pageSize={pageSize}
            setPageSize={setPageSize}
            listPage={listPage}
            setListPage={setListPage}
            totalCount={totalCount}
            pageCount={pageCount}
            visibleCount={visibleItems.length}
            label="Schüler"
            testId="bulk-enroll-pager"
            trailing={(
              <span className="text-slate-600">
                Ausgewählt: <strong>{selected.size}</strong>
              </span>
            )}
          />
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Lade Schüler ohne Belegung…
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[40vh] divide-y">
              <div className="sticky top-0 bg-slate-50 px-3 py-1.5 flex items-center gap-2 text-xs text-slate-600 border-b z-10">
                <Checkbox
                  checked={visibleAllSelected}
                  onCheckedChange={toggleVisible}
                  data-testid="bulk-enroll-select-page"
                />
                Sichtbare auswählen
                <Users className="w-3.5 h-3.5 ml-auto text-slate-400" />
              </div>
              {visibleItems.map((s) => (
                <label
                  key={s.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  data-testid={`bulk-enroll-row-${s.id}`}
                >
                  <Checkbox
                    checked={selected.has(s.id)}
                    onCheckedChange={() => toggle(s.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-slate-900 truncate">
                      {s.last_name}, {s.first_name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[s.postal_code, s.city].filter(Boolean).join(' ') || '—'}
                    </div>
                  </div>
                </label>
              ))}
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-slate-500">
                  Keine Schüler ohne aktive Belegung gefunden.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            disabled={saving || selected.size === 0 || !courseId}
            data-testid="bulk-enroll-submit"
            className="bg-brand-red hover:bg-brand-red/90 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            {selected.size || 0} zuordnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
